/**
 * Template réutilisable pour les landing pages SEO de Mediastore.
 */
import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, CheckCircle, ArrowRight, Shield, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/SEO";
import {
  PricingHeaderCell,
  PricingRow,
  FaqItem,
  FeatureCard,
  BrandItem,
} from "@/components/LandingTemplateParts";

const BUSINESS_PHONE = "05 49 05 74 90";
const BUSINESS_PHONE_LINK = "tel:+33549057490";

const ACCENT_RED     = { badge: "bg-red-100 text-red-700",         btn: "bg-red-600 hover:bg-red-700",         text: "text-red-700",     check: "text-red-600",     hover: "hover:bg-red-50",     bgIcon: "bg-red-100" };
const ACCENT_EMERALD = { badge: "bg-emerald-100 text-emerald-700", btn: "bg-emerald-600 hover:bg-emerald-700", text: "text-emerald-700", check: "text-emerald-600", hover: "hover:bg-emerald-50", bgIcon: "bg-emerald-100" };
const ACCENT_BLUE    = { badge: "bg-blue-100 text-blue-700",       btn: "bg-blue-600 hover:bg-blue-700",       text: "text-blue-700",    check: "text-blue-600",    hover: "hover:bg-blue-50",    bgIcon: "bg-blue-100" };
const ACCENT_PURPLE  = { badge: "bg-purple-100 text-purple-700",   btn: "bg-purple-600 hover:bg-purple-700",   text: "text-purple-700",  check: "text-purple-600",  hover: "hover:bg-purple-50",  bgIcon: "bg-purple-100" };
const ACCENT_AMBER   = { badge: "bg-amber-100 text-amber-700",     btn: "bg-amber-600 hover:bg-amber-700",     text: "text-amber-700",   check: "text-amber-600",   hover: "hover:bg-amber-50",   bgIcon: "bg-amber-100" };
const ACCENT_SLATE   = { badge: "bg-slate-100 text-slate-700",     btn: "bg-slate-900 hover:bg-slate-800",     text: "text-slate-700",   check: "text-slate-700",   hover: "hover:bg-slate-50",   bgIcon: "bg-slate-100" };

function pickAccent(color) {
  if (color === "emerald") return ACCENT_EMERALD;
  if (color === "blue") return ACCENT_BLUE;
  if (color === "purple") return ACCENT_PURPLE;
  if (color === "amber") return ACCENT_AMBER;
  if (color === "slate") return ACCENT_SLATE;
  return ACCENT_RED;
}

// Sous-composants : voir LandingTemplateParts.jsx
// (le plugin babel "visual-edits" d'Emergent plante sur les `.map()` imbriqués
// dans un même fichier ; en déplaçant ces sous-composants ailleurs, on évite
// le RangeError "Maximum call stack size exceeded" au build).

export const LandingTemplate = ({
  seo,
  hero,
  intro,
  features = [],
  pricing = null,
  brands = null,
  faq = [],
  finalCta,
  accentColor = "red",
}) => {
  const a = pickAccent(accentColor);
  const badgeCls = a.badge;
  const btnCls = a.btn;
  const textCls = a.text;
  const checkCls = a.check;
  const hoverCls = a.hover;
  const bgIconCls = a.bgIcon;

  // Variables locales pour éviter les double-member-expressions dans le JSX
  // (qui font boucler le plugin babel "visual-edits").
  const heroBadge = hero.badge;
  const heroH1 = hero.h1;
  const heroSubtitle = hero.subtitle;
  const heroFeaturesTitle = hero.featuresTitle || "Pourquoi nous choisir";
  const heroFeaturesSubtitle = hero.featuresSubtitle;

  const pricingBadge = pricing ? pricing.badge || "Tarifs" : null;
  const pricingTitle = pricing ? pricing.title : null;
  const pricingSubtitle = pricing ? pricing.subtitle : null;
  const pricingCaption = pricing ? pricing.caption || pricing.title : null;
  const pricingHeaders = pricing ? pricing.headers : [];
  const pricingRows = pricing ? pricing.rows : [];
  const pricingNote = pricing ? pricing.note : null;

  const brandsTitle = brands ? brands.title : null;
  const brandsSubtitle = brands ? brands.subtitle : null;
  const brandsItems = brands ? brands.items : [];

  const finalCtaTitle = finalCta && finalCta.title ? finalCta.title : "Prêt à réparer votre appareil ?";
  const finalCtaSubtitle = finalCta && finalCta.subtitle ? finalCta.subtitle : "Diagnostic gratuit en boutique. Devis transparent. Garantie 6 mois.";

  // Fonctions de rendu pour casser les double-member-expressions dans les .map()
  const renderFeature = (f, i) => (
    <FeatureCard
      key={`${f.title}-${i}`}
      index={i}
      Icon={f.Icon}
      title={f.title}
      description={f.description}
      bgIconCls={bgIconCls}
      textCls={textCls}
    />
  );
  const renderFaq = (item, i) => (
    <FaqItem
      key={`${item.q}-${i}`}
      index={i}
      q={item.q}
      a={item.a}
      hoverCls={hoverCls}
      textCls={textCls}
    />
  );

  return (
    <div className="bg-white" data-testid="landing-page">
      <SEO {...seo} />

      {/* HERO */}
      <section className="section-padding bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="container-custom max-w-5xl text-center">
          <Badge className={`${badgeCls} px-4 py-2 mb-6`}>{heroBadge}</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight" data-testid="landing-h1">
            {heroH1}
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
            {heroSubtitle}
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={BUSINESS_PHONE_LINK} data-testid="landing-phone-btn">
              <Button size="lg" className={`${btnCls} gap-2 text-base px-8`}>
                <Phone className="h-5 w-5" />
                {BUSINESS_PHONE}
              </Button>
            </a>
            <Link to="/rendez-vous" data-testid="landing-appointment-btn">
              <Button size="lg" variant="outline" className="gap-2 text-base px-8">
                Prendre rendez-vous
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 text-sm text-slate-600">
            <div className="flex items-center justify-center gap-2"><Shield className={`h-4 w-4 ${checkCls}`} /> Garantie 6 mois</div>
            <div className="flex items-center justify-center gap-2"><CheckCircle className={`h-4 w-4 ${checkCls}`} /> Diagnostic gratuit</div>
            <div className="flex items-center justify-center gap-2"><Clock className={`h-4 w-4 ${checkCls}`} /> Express dès 30 min</div>
            <div className="flex items-center justify-center gap-2"><Star className={`h-4 w-4 ${checkCls}`} /> 4,6/5 (212 avis)</div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed space-y-4">
            {intro.map((paragraph) => (
              <p key={paragraph.slice(0, 40)} className="text-base md:text-lg">{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      {features.length > 0 && (
        <section className="section-padding bg-slate-50">
          <div className="container-custom max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {heroFeaturesTitle}
              </h2>
              <p className="text-lg text-slate-600">{heroFeaturesSubtitle}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map(renderFeature)}
            </div>
          </div>
        </section>
      )}

      {/* BRANDS */}
      {brands && (
        <section className="section-padding bg-white">
          <div className="container-custom max-w-4xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{brandsTitle}</h2>
              <p className="text-lg text-slate-600">{brandsSubtitle}</p>
            </div>
            <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 list-none p-0">
              {brandsItems.map((item) => (
                <BrandItem key={item} name={item} hoverCls={hoverCls} />
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* PRICING TABLE */}
      {pricing && (
        <section className="section-padding bg-slate-50" data-testid="landing-pricing">
          <div className="container-custom max-w-5xl">
            <div className="text-center mb-10">
              <Badge className={`${badgeCls} px-4 py-2 mb-4`}>{pricingBadge}</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{pricingTitle}</h2>
              <p className="text-lg text-slate-600">{pricingSubtitle}</p>
            </div>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
              <table className="w-full">
                <caption className="sr-only">{pricingCaption}</caption>
                <thead className="bg-slate-900 text-white">
                  <tr>
                    {pricingHeaders.map((h, i) => (
                      <PricingHeaderCell key={h} label={h} isFirst={i === 0} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pricingRows.map((row) => (
                    <PricingRow key={row[0]} cells={row} hoverCls={hoverCls} textCls={textCls} />
                  ))}
                </tbody>
              </table>
            </div>
            {pricingNote && <p className="text-center text-sm text-slate-500 mt-6">{pricingNote}</p>}
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="section-padding bg-white" data-testid="landing-faq">
          <div className="container-custom max-w-4xl">
            <div className="text-center mb-12">
              <Badge className={`${badgeCls} px-4 py-2 mb-4`}>FAQ</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Questions fréquentes</h2>
            </div>
            <ol className="space-y-4 list-none p-0">
              {faq.map(renderFaq)}
            </ol>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container-custom max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {finalCtaTitle}
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            {finalCtaSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={BUSINESS_PHONE_LINK} data-testid="final-cta-phone">
              <Button size="lg" className={`${btnCls} gap-2 text-base px-8`}>
                <Phone className="h-5 w-5" />
                {BUSINESS_PHONE}
              </Button>
            </a>
            <Link to="/contact" data-testid="final-cta-contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 gap-2 text-base px-8">
                <MapPin className="h-5 w-5" />
                Venir en boutique
              </Button>
            </Link>
          </div>
          <p className="text-sm text-slate-400 mt-6">
            104 Rue de la Gare, 79000 Niort - Lun-Ven 10h-18h, Sam 10h-16h
          </p>
        </div>
      </section>
    </div>
  );
};
